import React, { useState, useEffect } from "react";
import CommonLoader from "../common/CommonLoader";
import BottleOrdersTable from "./BottleOrdersTable";
import { GetBottleOrders } from "../../services/BottleOrdersService";
import { NotificationManager } from "react-notifications";
import { bottleOrderStatusList } from "../common/statusLists";
import {
  FormControl,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Tooltip,
  Chip,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";

export default function BottleOrders() {
  const [loading, setLoading] = useState(false);
  const [bottleOrdersData, setBottleOrdersData] = useState(null);
  const [bottleOrdersCount, setBottleOrdersCount] = useState(0);
  const [dateFilter, setDateFilter] = useState("DateCreated");

  const [searchCriteria, setSearchCriteria] = useState({
    searchText: "",
    orderStatus: "",
    startDate: "",
    endDate: "",
    pageNumber: 0,
    pageSize: 20,
  });

  const navigate = useNavigate();

  // Which date column From/To applies to.
  const dateFilterOptions = [
    { value: "DateCreated", label: "Created" },
    { value: "KitPrepDueDate", label: "Kit Prep Due" },
    { value: "DueDate", label: "Due" },
  ];

  useEffect(() => {
    const today = new Date();

    const startDateValue = new Date(today);
    startDateValue.setDate(today.getDate() - 15);
    const startDateStr = startDateValue.toISOString().split("T")[0];

    const endDateValue = new Date(today);
    endDateValue.setDate(today.getDate() + 15);
    const endDateStr = endDateValue.toISOString().split("T")[0];

    const initialCriteria = {
      searchText: "",
      orderStatus: "",
      startDate: startDateStr,
      endDate: endDateStr,
      pageNumber: 0,
      pageSize: 20,
    };

    setSearchCriteria(initialCriteria);
    getBottleOrders(initialCriteria);
  }, []);

  const getBottleOrders = async (criteria) => {
    try {
      setLoading(true);
      let response = await GetBottleOrders(
        criteria.startDate,
        criteria.endDate,
        criteria.orderStatus,
        criteria.searchText,
        criteria.pageNumber,
        dateFilter,
        criteria.pageSize
      );
      setBottleOrdersData(response.results);
      setBottleOrdersCount(response.count);
    } catch (err) {
      NotificationManager.error("Error getting orders. " + err, "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusSelect = (status) => {
    const current = searchCriteria.orderStatus
      ? searchCriteria.orderStatus.split(",").filter((s) => s.trim() !== "")
      : [];
    if (!current.includes(status)) {
      const updated = [...current, status].join(",");
      setSearchCriteria({ ...searchCriteria, orderStatus: updated });
    }
  };

  const handleRemoveStatus = (status) => {
    const updated = searchCriteria.orderStatus
      .split(",")
      .filter((s) => s.trim() !== "" && s !== status)
      .join(",");
    setSearchCriteria({ ...searchCriteria, orderStatus: updated });
  };

  const getSelectedStatuses = () => {
    if (!searchCriteria.orderStatus) return [];
    return searchCriteria.orderStatus.split(",").filter((s) => s.trim() !== "");
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    if (searchCriteria.startDate === "" || searchCriteria.endDate === "") {
      NotificationManager.error("Invalid Dates");
      return;
    }

    const updatedSearchCriteria = {
      ...searchCriteria,
      pageNumber: 0,
    };

    setSearchCriteria(updatedSearchCriteria);
    getBottleOrders(updatedSearchCriteria);
  };

  // Called by the table whenever page or rowsPerPage changes
  const onChangePageSettings = async (pageNumber, pageSize) => {
    const updatedSearchCriteria = {
      ...searchCriteria,
      pageNumber,
      pageSize,
    };
    setSearchCriteria(updatedSearchCriteria);
    getBottleOrders(updatedSearchCriteria);
  };

  return (
    <div className="vlp-component-container">
      {/* New sits on the title row (secondary style), same as Supply Orders. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span className="title">Bottle Orders</span>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate("/vlp/bottle-orders/add")}
        >
          New
        </Button>
      </div>
      <hr />

      {/* Search Controls */}
      <div className="columns">
        <div className="column">
          <form onSubmit={handleSearchSubmit}>
            <Grid container spacing={2} alignItems="center">
              {/* Search Text */}
              <Grid item xs={12} sm={3}>
                <Tooltip
                  title="Search by shipping status, account number, shipping name, shipping contact, description, comments, or order number"
                  arrow
                  placement="top"
                >
                  <TextField
                    label="Search Criteria"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchCriteria.searchText}
                    onChange={(e) =>
                      setSearchCriteria({ ...searchCriteria, searchText: e.target.value })
                    }
                  />
                </Tooltip>
              </Grid>

              {/* From Date */}
              <Grid item xs={12} sm={1.75}>
                <Tooltip
                  title="Filter by created date, order date, shipping date, kit prep due date, due date, date received, cooler received date, prepared-by date, reviewed-by date, or order-reviewed date"
                  arrow
                  placement="top"
                >
                  <TextField
                    label="From Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchCriteria.startDate}
                    onChange={(e) =>
                      setSearchCriteria({ ...searchCriteria, startDate: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Tooltip>
              </Grid>

              {/* To Date */}
              <Grid item xs={12} sm={1.75}>
                <Tooltip
                  title="Filter by created date, order date, shipping date, kit prep due date, due date, date received, cooler received date, prepared-by date, reviewed-by date, or order-reviewed date"
                  arrow
                  placement="top"
                >
                  <TextField
                    label="To Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchCriteria.endDate}
                    onChange={(e) =>
                      setSearchCriteria({ ...searchCriteria, endDate: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Tooltip>
              </Grid>

              {/* Multi-Status Select */}
              <Grid item xs={12} sm={2.5}>
                <Tooltip
                  title="Select one or multiple statuses of the order"
                  arrow
                  placement="top"
                >
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel shrink>Status</InputLabel>
                    <Select
                      value=""
                      onChange={(e) => handleStatusSelect(e.target.value)}
                      label="Status"
                      displayEmpty
                      notched
                      renderValue={() => {
                        const count = getSelectedStatuses().length;
                        return count > 0
                          ? `${count} status${count > 1 ? "es" : ""} selected`
                          : "Select statuses";
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select statuses</em>
                      </MenuItem>
                      {bottleOrderStatusList.map((status) => (
                        <MenuItem
                          key={status}
                          value={status}
                          disabled={getSelectedStatuses().includes(status)}
                        >
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>
              </Grid>

              {/* Date filter on */}
              <Grid item xs={12} sm={2}>
                <Tooltip
                  title="Choose which date the From / To range filters on"
                  arrow
                  placement="top"
                >
                  <TextField
                    select
                    label="Date filter on"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={dateFilter}
                    onChange={handleDateFilterChange}
                  >
                    {dateFilterOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Tooltip>
              </Grid>

              {/* Search button */}
              <Grid item xs={12} sm={1}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="medium"
                  style={{ height: "40px" }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </form>

          {getSelectedStatuses().length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 2 }}>
              <strong style={{ fontSize: "0.875rem" }}>Selected Statuses:</strong>
              {getSelectedStatuses().map((status) => (
                <Chip
                  key={status}
                  label={status}
                  onDelete={() => handleRemoveStatus(status)}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          )}
        </div>
      </div>

      {bottleOrdersData !== null && (
        <BottleOrdersTable
          bottleOrdersData={bottleOrdersData}
          count={bottleOrdersCount}
          onChange={onChangePageSettings}
        />
      )}
      <CommonLoader loading={loading} />
    </div>
  );
}